import { LandPlot, MapPin } from "lucide-react";
import { useLocale, useTranslations } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';

export default function EventCard({ item }: any) {
  const locale = useLocale();
  const t = useTranslations();
  const { getLocalizedField } = useLocalizedField();
  
  const title = getLocalizedField(item, 'title', item.title || 'Unknown');
  const address = getLocalizedField(item, 'address', item.address || 'Unknown');
  
  const startDate = new Date(item.start_date);
  const endDate = new Date(item.end_date);
  const today = new Date();

  // Format dates with locale
  const startMonth = startDate.toLocaleString(locale === 'ar' ? 'ar-TN' : locale, { month: "short" });
  const startDay = startDate.getDate();

  const endMonth = endDate.toLocaleString(locale === 'ar' ? 'ar-TN' : locale, { month: "short" });
  const endDay = endDate.getDate();

  const isSameDate = item.start_date === item.end_date;

  const country = address?.split(",").pop()?.trim() || "Tunisia";

  // Status logic
  let status = "";
  let statusColor = "";

  if (today < startDate) {
    status = t('events.pending');
    statusColor = "bg-yellow-500";
  } else if (today >= startDate && today <= endDate) {
    status = t('events.ongoing');
    statusColor = "bg-green-600";
  } else {
    status = t('events.past');
    statusColor = "bg-red-600";
  }

  return (
    <a href={`/${locale}/events/${item.slug}`} className="bg-white rounded-3xl shadow-md overflow-hidden w-full max-w-sm mx-auto relative" dir={locale === 'ar' ? 'rtl' : 'ltr'}>

      {/* Status Badge */}
      <div className={`absolute top-3 z-10 ${locale === 'ar' ? 'right-3' : 'left-3'}`}>
        <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${statusColor}`}>
          {status}
        </span>
      </div>

      {/* Image */}
      <div className="w-full h-48 overflow-hidden">
        <img
          src={item.cover}
          alt={title}
          className="w-full h-full object-cover rounded-3xl border-b-4 border-primary"
        />
      </div>

      {/* Content */}
      <div className={`p-5 flex gap-5 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>

        {/* Date */}
        <div className="text-center min-w-[55px] flex flex-col items-center justify-center">
          
          {/* Start */}
          <div>
            <p className="text-sm font-medium text-gray-600">{startMonth}</p>
            <p className="text-3xl font-bold text-red-900 leading-none">{startDay}</p>
          </div>

          {/* Separator */}
          {!isSameDate && <div className="w-6 my-1 border-t border-gray-300"></div>}

          {/* End */}
          {!isSameDate && (
            <div>
              <p className="text-sm font-medium text-gray-600">{endMonth}</p>
              <p className="text-3xl font-bold text-red-900 leading-none">{endDay}</p>
            </div>
          )}
        </div>

        <div className="w-px bg-gray-300"></div>

        {/* Info */}
        <div className="flex flex-col">
          <p className={`text-gray-500 text-sm flex items-center gap-1 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
            <LandPlot /> {country}
          </p>

          <h3 className="text-xl font-bold text-gray-800 mt-1">{title}</h3>

          <p className={`text-gray-500 text-xs mt-2 flex items-center gap-1 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
            <MapPin /> {address}
          </p>
        </div>

      </div>
    </a>
  );
}
